import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery,
  Backdrop,
  CircularProgress,
  Slide,
  IconButton,
} from "@mui/material";

import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import UpdateRoundedIcon from "@mui/icons-material/UpdateRounded";
import { ReactComponent as ExcelExportIcon } from "../assets/img/icons/excel_export.svg";
import * as XLSX from "xlsx";
import { SecureImage } from "./SecureImage";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3000";

export default function WarehouseDashboard() {
  const [mainTab, setMainTab] = useState("Active");
  const [historyTab, setHistoryTab] = useState("Accepted");
  const [currentTab, setCurrentTab] = useState("Active");
  const [loading, setLoading] = useState(false);
  const [ordersData, setOrdersData] = useState([]);
  const [isLogoutPopup, setIsLogoutPopup] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const navigate = useNavigate();

  // Endpoints according to selected tab
  const getStatusForAPI = () => {
    if (mainTab === "Active") return "Confirm(DM)";

    if (mainTab === "History" && historyTab === "Accepted") return "Accept(WH)";

    if (mainTab === "History" && historyTab === "Unavailable")
      return "Unavailable(WH)";

    return "Confirm(DM)";
  };

  // Fetch orders function
  const fetchOrderData = async () => {
    setLoading(true);

    try {
      const status = getStatusForAPI();
      const url = `${API_BASE_URL}/api/fetchorder/getOrders?status=${status}`;

      const { data } = await axios.get(url);
      const records = data?.data?.records || [];

      setOrdersData(records);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  // When currentTab changes, immediately clear previous data and trigger fetch.
  useEffect(() => {
    setOrdersData([]);
    setLoading(true);
    fetchOrderData();
  }, [mainTab, historyTab]);

  // Process orders data to create stock items with metadata
  const processedStockData = useMemo(() => {
    if (!ordersData || ordersData.length === 0) return [];

    const allItems = [];

    ordersData.forEach((order) => {
      if (
        order.selectedTagElements &&
        Array.isArray(order.selectedTagElements)
      ) {
        order.selectedTagElements.forEach((item) => {
          allItems.push({
            ...item,
            orderStatus: order.status,
            orderType: order.orderType,
            order_no: order.order_no,
            linkId: order.linkId,
            customerName: order.customerName,
            customerPhone: order.customerPhone,
            deliveryDate: order.deliveryDate,
            store_id: order.store_id,
            flag: order.flag || "i",
          });
        });
      }
    });

    return allItems;
  }, [ordersData]);

  // Group items into sets based on crosstag logic
  const groupItemsIntoSets = (items) => {
    if (!items || items.length === 0) return [];

    // Get flag from first item
    const orderFlag = items[0]?.flag || "i";

    if (orderFlag === "i") {
      return items.map((item) => ({
        type: "individual",
        parent: item,
        children: [],
      }));
    }

    const sets = [];
    const processedTags = new Set();

    items.forEach((item) => {
      if (processedTags.has(item.tagno)) return;

      const children = items.filter(
        (child) => child.crosstag === item.tagno && child.tagno !== item.tagno
      );

      if (children.length > 0) {
        sets.push({
          type: "set",
          parent: item,
          children: children,
        });

        processedTags.add(item.tagno);
        children.forEach((ch) => processedTags.add(ch.tagno));
      } else if (!items.some((parent) => parent.tagno === item.crosstag)) {
        sets.push({
          type: "individual",
          parent: item,
          children: [],
        });
        processedTags.add(item.tagno);
      }
    });

    return sets;
  };

  const groupedData = useMemo(
    () => groupItemsIntoSets(processedStockData),
    [processedStockData]
  );

  // Update status function
  const handleUpdateStatus = async (group, status) => {
    const allItems = [group.parent, ...group.children];
    const stockItemTags = allItems.map((item) => item.tagno);

    try {
      const payload = {
        linkId: group.parent.linkId,
        orderNo: group.parent.order_no,
        selectedTags: stockItemTags,
        customerDetails: {
          customerName: group.parent.customerName,
          customerPhone: group.parent.customerPhone,
          deliveryDate: group.parent.deliveryDate,
        },
        mfrId: group.parent.store_id,
        status: status,
        rejectedBy: "",
        rejectedReason: "",
      };

      await axios.post(
        `${API_BASE_URL}/api/fetchorder/changeOrderStatus`,
        payload
      );

      await fetchOrderData();
    } catch (err) {
      console.log(err);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    const prefix = localStorage.getItem("urlPrefix");
    if (prefix) {
      navigate(`/${prefix}`);
    } else {
      navigate("/login");
    }
  };

  const exportGroupedToExcel = () => {
    if (!groupedData || groupedData.length === 0) {
      alert("No data available to export");
      return;
    }

    const exportRows = [];

    groupedData.forEach((group) => {
      const allItems = [group.parent, ...group.children];

      allItems.forEach((item) => {
        exportRows.push({
          Order_No: item.order_no,
          Tag_No: item.tagno,
          Item: item.itemtype_name,
          Design: item.design_name,
          Gross: item.gross,
          Status: item.orderStatus,
          Customer_Name: item.customerName,
          Customer_Phone: item.customerPhone,
          Delivery_Date: item.deliveryDate,
        });
      });
    });

    // Convert JSON → worksheet
    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

    // File name based on tab
    const filename =
      mainTab === "Active"
        ? "Active_Orders.xlsx"
        : historyTab === "Accepted"
        ? "Accepted_Orders.xlsx"
        : "Unavailable_Orders.xlsx";

    XLSX.writeFile(workbook, filename);
  };

  return (
    <Box
      sx={{
        width: "100%",
        bgcolor: "#344758",
        minHeight: "100vh",
        p: 4,
        position: "relative",
      }}
    >
      {/* Backdrop loader overlay */}
      <Backdrop
        open={loading}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 10,
          color: "#fff",
          flexDirection: "column",
        }}
      >
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading...</Typography>
      </Backdrop>

      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          flexDirection: "column",
          gap: 2,
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: "100%",
            justifyContent: "space-between",
          }}
        >
          <Typography
            sx={{
              color: "#fff",
              fontSize: isMobile ? 24 : 32,
              fontWeight: 600,
            }}
          >
            WAREHOUSE DASHBOARD
          </Typography>
          <Box
            onClick={() => setIsLogoutPopup(true)}
            sx={{ display: "flex", gap: 1, cursor: "pointer" }}
          >
            <Typography sx={{ color: "#ffff" }}>Logout</Typography>
            <LogoutRoundedIcon sx={{ color: "#fff" }} />
          </Box>
        </Box>
      </Box>

      {/* Active / History Tabs & Excel Export Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        {/* Active / History Tabs */}
        <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
          {/* Active TAB */}
          <Box
            onClick={() => setMainTab("Active")}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              bgcolor: mainTab === "Active" ? "#008085" : "#4e4e4e",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <NotificationsActiveRoundedIcon
              sx={{
                fontSize: 18,
                color: mainTab === "Active" ? "#fff" : "#9e9e9e",
              }}
            />
            <Typography
              sx={{ color: mainTab === "Active" ? "#fff" : "#9e9e9e" }}
            >
              Active Orders
            </Typography>
          </Box>

          {/* History TAB */}
          <Box
            onClick={() => setMainTab("History")}
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              cursor: "pointer",
              bgcolor: mainTab === "History" ? "#008085" : "#4e4e4e",
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <UpdateRoundedIcon
              sx={{
                fontSize: 18,
                color: mainTab === "History" ? "#fff" : "#9e9e9e",
              }}
            />
            <Typography
              sx={{ color: mainTab === "History" ? "#fff" : "#9e9e9e" }}
            >
              History
            </Typography>
          </Box>

          {/* SHOW SUBTABS ONLY IF mainTab = "History" */}
          {mainTab === "History" && (
            <>
              {/* SHOW SUBTABS ONLY IF mainTab = "History" */}
              <Slide
                direction="left"
                in={mainTab === "History"}
                mountOnEnter
                unmountOnExit
                timeout={300} // smooth speed
              >
                <Box sx={{ display: "flex", gap: 2 }}>
                  {/* Accepted sub-tab */}
                  <Box
                    onClick={() => setHistoryTab("Accepted")}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      bgcolor:
                        historyTab === "Accepted" ? "#00a99d" : "#4e4e4e",
                      transition: "0.25s",
                    }}
                  >
                    <Typography sx={{ color: "#fff" }}>Accepted</Typography>
                  </Box>

                  {/* Unavailable sub-tab */}
                  <Box
                    onClick={() => setHistoryTab("Unavailable")}
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: 2,
                      cursor: "pointer",
                      bgcolor:
                        historyTab === "Unavailable" ? "#00a99d" : "#4e4e4e",
                      transition: "0.25s",
                    }}
                  >
                    <Typography sx={{ color: "#fff" }}>Unavailable</Typography>
                  </Box>
                </Box>
              </Slide>
            </>
          )}
        </Box>

        {/* Excel Export */}
        <Box>
          <IconButton
            onClick={exportGroupedToExcel}
            sx={{
              bgcolor: "#FFF",
              borderRadius: 2,
              width: 36,
              height: 36,
              "&:hover": {
                bgcolor: "#1c4b42ff",
                "& svg": {
                  fill: "#ffffff", // <-- icon color on hover
                },
              },
              "& svg": {
                transition: "0.2s",
                fill: "#42A996", // <-- default icon color
              },
            }}
            aria-label="Export Excel"
          >
            <ExcelExportIcon
              style={{
                width: 18,
                height: 18,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* TABLE */}
      <Box
        sx={{
          bgcolor: "#2A3A4B",
          borderRadius: 2,
          overflowX: { xs: "auto", md: "visible" },
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "& table": {
            width: "max-content",
            minWidth: "100%",
            tableLayout: "auto !important",
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Order No
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Tag No
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Item
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Design
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Gross
              </TableCell>
              <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                Image
              </TableCell>
              {mainTab === "Active" && (
                <TableCell sx={{ color: "#fff", fontWeight: 600 }}>
                  Action
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {/* Loading */}
            {loading && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{ textAlign: "center", color: "#fff", py: 4 }}
                >
                  {/* We still show a row message so table height remains consistent.
                      The Backdrop overlay visually blocks interaction anyway. */}
                  Loading...
                </TableCell>
              </TableRow>
            )}

            {/* No Order Found */}
            {!loading && groupedData.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{ textAlign: "center", color: "#fff", py: 4 }}
                >
                  No orders found
                </TableCell>
              </TableRow>
            )}

            {/* Showing Data */}
            {!loading &&
              groupedData.map((group, groupIndex) => {
                const isSet = group.type === "set";
                const allItems = [group.parent, ...group.children];

                return (
                  <TableRow
                    key={groupIndex}
                    sx={{
                      bgcolor: groupIndex % 2 === 0 ? "#1E2F40" : "#2A3A4B",
                      "& td": {
                        paddingY: 1,
                        paddingX: 0,
                        verticalAlign: "middle",
                      },
                    }}
                  >
                    {/* Order No */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      <Box
                        sx={{
                          minHeight: 70,
                          display: "flex",
                          alignItems: "center",
                          px: 2,
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          {group.parent.order_no}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Tag column */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: index === 0 && isSet ? 600 : 400,
                              color: index === 0 && isSet ? "#4CAF50" : "#fff",
                            }}
                          >
                            {item.tagno}
                            {index === 0 && isSet && (
                              <Typography
                                component="span"
                                sx={{
                                  ml: 1,
                                  fontSize: 10,
                                  bgcolor: "#4CAF50",
                                  color: "#fff",
                                  px: 0.5,
                                  py: 0.2,
                                  borderRadius: 0.5,
                                }}
                              >
                                P
                              </Typography>
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </TableCell>

                    {/* Item */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {item.itemtype_name}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Design */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {item.design_name}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Gross */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {parseFloat(item.gross).toFixed(3)}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Image */}
                    <TableCell
                      sx={{ color: "#fff", verticalAlign: "top", p: 0 }}
                    >
                      {allItems.map((item, index) => (
                        <Box
                          key={index}
                          sx={{
                            minHeight: 80,
                            display: "flex",
                            alignItems: "center",
                            px: 2,
                            borderBottom:
                              index < allItems.length - 1
                                ? "1px solid rgba(255,255,255,0.06)"
                                : "none",
                          }}
                        >
                          {item.imageUrl ? (
                            <SecureImage
                              item={item}
                              width={80}
                              height={80}
                              borderRadius={2}
                            />
                          ) : (
                            <Typography sx={{ color: "#aaa" }}>--</Typography>
                          )}
                        </Box>
                      ))}
                    </TableCell>

                    {/* Actions */}
                    {mainTab === "Active" && (
                      <TableCell sx={{ verticalAlign: "top", p: 0 }}>
                        <Box
                          sx={{
                            minHeight: 70,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            px: 2,
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="contained"
                            sx={{
                              borderRadius: 1.5,
                              height: "32px",
                              bgcolor: "#1CA19B",
                              width: "120px",
                              textTransform: "none",
                              "&:hover": { bgcolor: "#158e89" },
                            }}
                            onClick={() =>
                              handleUpdateStatus(group, "Accept(WH)")
                            }
                          >
                            Accept
                          </Button>

                          <Button
                            variant="contained"
                            sx={{
                              borderRadius: 1.5,
                              height: "32px",
                              bgcolor: "#445571",
                              width: "120px",
                              textTransform: "none",
                              "&:hover": { bgcolor: "#364460" },
                            }}
                            onClick={() =>
                              handleUpdateStatus(group, "Unavailable(WH)")
                            }
                          >
                            Unavailable
                          </Button>
                        </Box>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Box>

      {/* Logout Dialog */}
      <Dialog
        open={isLogoutPopup}
        onClose={() => setIsLogoutPopup(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Logout</DialogTitle>

        <DialogContent>
          <Typography>Are you sure you want to logout?</Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setIsLogoutPopup(false)}
            variant="outlined"
            color="primary"
          >
            Cancel
          </Button>
          <Button onClick={handleLogout} variant="contained" color="error">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
